// Forgot password page — two-phase reset flow: enter email, then enter code + new password.
import { useState } from 'react';
import { Link } from 'react-router';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Button, Alert } from '../components/common';
import { useTheme } from '../contexts/ThemeContext';
import logoPrimary from '../assets/contractgen-logo-primary.svg';
import logoPrimaryDark from '../assets/contractgen-logo-primary-dark.svg';
import { authPageWrapper, authCard, authLogoClass } from './authLayout';

type Phase = 'request' | 'confirm' | 'success';

export default function ForgotPassword() {
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === 'dark' ? logoPrimaryDark : logoPrimary;

  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await resetPassword({ username: email.trim() });
      setPhase('confirm');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send reset code'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code,
        newPassword,
      });
      setPhase('success');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to reset password'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    try {
      await resetPassword({ username: email.trim() });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to resend code'
      );
    }
  };

  if (phase === 'success') {
    return (
      <div className={authPageWrapper}>
        <div className={`${authCard} space-y-8 text-center`}>
          <img src={logo} alt="ContractGen" className={authLogoClass} />
          <h1 className="text-h1 text-slate-900 dark:text-slate-50">
            Password has been reset
          </h1>
          <p className="mt-4 text-body text-slate-600 dark:text-slate-400">
            You can now sign in with your new password.
          </p>
          <Link
            to="/login"
            className="mt-4 inline-block text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (phase === 'confirm') {
    return (
      <div className={authPageWrapper}>
        <div className={`${authCard} space-y-8`}>
          <div className="text-center">
            <img src={logo} alt="ContractGen" className={authLogoClass} />
            <h1 className="text-h1 text-slate-900 dark:text-slate-50">
              Reset your password
            </h1>
            <p className="mt-2 text-body text-slate-600 dark:text-slate-400">
              Enter the verification code sent to your email and choose a new
              password.
            </p>
          </div>

          <form onSubmit={handleConfirmReset} className="mt-8 space-y-6">
            {error && <Alert variant="error">{error}</Alert>}

            <div className="space-y-4">
              <div>
                <label htmlFor="verification-code" className="label">
                  Verification Code
                </label>
                <input
                  id="verification-code"
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="input"
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
              </div>

              <div>
                <label htmlFor="new-password" className="label">
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label htmlFor="confirm-new-password" className="label">
                  Confirm New Password
                </label>
                <input
                  id="confirm-new-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <Button type="submit" loading={isLoading} className="w-full">
              Reset Password
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                className="text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium text-body-sm"
              >
                Resend Code
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={authPageWrapper}>
      <div className={`${authCard} space-y-8`}>
        <div className="text-center">
          <img src={logo} alt="ContractGen" className={authLogoClass} />
          <h1 className="text-h1 text-slate-900 dark:text-slate-50">
            Forgot password
          </h1>
          <p className="mt-2 text-body text-slate-600 dark:text-slate-400">
            Enter your email address and we&apos;ll send you a code to reset
            your password.
          </p>
        </div>

        <form onSubmit={handleRequestReset} className="mt-8 space-y-6">
          {error && <Alert variant="error">{error}</Alert>}

          <div>
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
            />
          </div>

          <Button type="submit" loading={isLoading} className="w-full">
            {isLoading ? 'Sending...' : 'Send Reset Code'}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium text-body-sm"
            >
              Back to Sign In
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
